import { Fragment, useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, apiWrite } from '../lib/api'
import { PageHead, Skel } from '../components/ui'

type RoleKey = 'owner' | 'admin' | 'audit_manager' | 'auditor' | 'viewer'

interface PermissionInfo {
  code: string
  label_th: string
  group_th: string
}

interface MatrixCell {
  role: RoleKey
  permission: string
  allowed: boolean
  default: boolean
  overridden: boolean
}

interface Warning {
  code: string
  severity: 'high' | 'medium' | string
  role: RoleKey
  message_th: string
  permissions: string[]
}

interface MatrixData {
  roles: RoleKey[]
  permissions: PermissionInfo[]
  cells: MatrixCell[]
  user_counts: Record<string, number>
  warnings: Warning[]
}

const roleMeta: Record<RoleKey, { label: string; hint: string }> = {
  owner: { label: 'เจ้าขององค์กร', hint: 'สิทธิ์ทั้งหมด แก้ไขไม่ได้' },
  admin: { label: 'ผู้ดูแลระบบ', hint: 'ดูแลผู้ใช้ คีย์ และการตั้งค่า' },
  audit_manager: { label: 'ผู้จัดการตรวจสอบ', hint: 'อนุมัติและควบคุมคุณภาพงานตรวจสอบ' },
  auditor: { label: 'ผู้ตรวจสอบ', hint: 'ทำงานภาคสนามและสร้างข้อสังเกต' },
  viewer: { label: 'ผู้ชม', hint: 'อ่านอย่างเดียว' },
}

const roleLabel = (role: string) => roleMeta[role as RoleKey]?.label ?? role

const severityLabel = (severity: string) => (severity === 'high' ? 'เสี่ยงสูง' : 'ควรทบทวน')

export function Roles() {
  const qc = useQueryClient()
  const [error, setError] = useState<string | null>(null)

  const matrix = useQuery({
    queryKey: ['roles-matrix'],
    queryFn: () => api<MatrixData>('/v1/roles/matrix'),
    staleTime: 10_000,
  })

  const setPermission = useMutation<
    { role: RoleKey; permission: string; allowed: boolean },
    Error,
    { role: RoleKey; permission: string; allowed: boolean },
    { prev?: MatrixData }
  >({
    mutationFn: (v) =>
      apiWrite<{ role: RoleKey; permission: string; allowed: boolean }>(
        `/v1/roles/${v.role}/permissions/${encodeURIComponent(v.permission)}`, 'PUT', { allowed: v.allowed }),
    onMutate: async (v) => {
      await qc.cancelQueries({ queryKey: ['roles-matrix'] })
      const prev = qc.getQueryData<MatrixData>(['roles-matrix'])
      if (prev) {
        qc.setQueryData<MatrixData>(['roles-matrix'], {
          ...prev,
          cells: prev.cells.map((cell) =>
            cell.role === v.role && cell.permission === v.permission
              ? { ...cell, allowed: v.allowed, overridden: v.allowed !== cell.default }
              : cell),
        })
      }
      return { prev }
    },
    onError: (e, _v, ctx) => {
      if (ctx?.prev) qc.setQueryData(['roles-matrix'], ctx.prev)
      setError(e.message || 'บันทึกสิทธิ์ไม่สำเร็จ')
    },
    onSettled: () => { void qc.invalidateQueries({ queryKey: ['roles-matrix'] }) },
  })

  const data = matrix.data
  const cellMap = useMemo(() => {
    const map = new Map<string, MatrixCell>()
    for (const cell of data?.cells ?? []) map.set(`${cell.role}:${cell.permission}`, cell)
    return map
  }, [data])

  const groups = useMemo(() => {
    const map = new Map<string, PermissionInfo[]>()
    for (const p of data?.permissions ?? []) {
      const list = map.get(p.group_th) ?? []
      list.push(p)
      map.set(p.group_th, list)
    }
    return Array.from(map.entries())
  }, [data])

  const warnings = data?.warnings ?? []
  const roles = data?.roles ?? (Object.keys(roleMeta) as RoleKey[])

  const allowedCount = (role: RoleKey) =>
    (data?.cells ?? []).filter((c) => c.role === role && c.allowed).length
  const warningsFor = (role: RoleKey) => warnings.filter((w) => w.role === role).length

  const toggle = (role: RoleKey, permission: string, allowed: boolean) => {
    if (role === 'owner') return
    setError(null)
    setPermission.mutate({ role, permission, allowed })
  }

  return (
    <>
      <PageHead
        title="บทบาทและสิทธิ์"
        sub="กำหนดสิทธิ์รายบทบาท · ระบบตรวจสอบหลักความจำเป็นน้อยที่สุดให้อัตโนมัติ"
      />

      <div className="card card-pad" style={{ marginBottom: 18 }}>
        <div className="row" style={{ flexWrap: 'wrap' }}>
          <span className="card-label">ผลตรวจหลักความจำเป็นน้อยที่สุด</span>
          <span className={`chip${warnings.length ? ' gold' : ''}`} style={{ marginLeft: 'auto' }}>
            {warnings.length ? `${warnings.length} รายการ` : 'ไม่พบความเสี่ยง'}
          </span>
        </div>

        {matrix.isLoading ? <Skel h={90} /> : warnings.length === 0 ? (
          <p className="dim" style={{ fontSize: 12.5, marginTop: 12, lineHeight: 1.7 }}>
            ทุกบทบาทมีสิทธิ์เท่าที่จำเป็น ไม่พบบทบาทที่ถือสิทธิ์ของเจ้าขององค์กร
            หรือรวมสิทธิ์เขียนและอนุมัติไว้ในบทบาทเดียวกัน
          </p>
        ) : (
          <div style={{ marginTop: 12, display: 'grid', gap: 10 }}>
            {warnings.map((w, i) => (
              <div key={`${w.role}-${w.code}-${i}`} style={{
                border: '1px solid var(--color-hairline)', borderRadius: 6,
                padding: '10px 12px', background: 'var(--color-wash)',
              }}>
                <div className="row" style={{ gap: 10, flexWrap: 'wrap' }}>
                  <span className={`sev sev-${w.severity}`}>
                    <i className="sev-dot" />
                    {severityLabel(w.severity)}
                  </span>
                  <span style={{ fontSize: 12.5 }}>{w.message_th}</span>
                  <span className="chip" style={{ marginLeft: 'auto' }}>{roleLabel(w.role)}</span>
                </div>
                <div className="row" style={{ gap: 10, marginTop: 8, flexWrap: 'wrap' }}>
                  {w.permissions.map((p) => (
                    <span key={p} className="mono dim" style={{ fontSize: 10.5 }}>{p}</span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="row" style={{ marginBottom: 10, flexWrap: 'wrap' }}>
        <span className="card-label">เมทริกซ์สิทธิ์ · {roles.length} บทบาท × {data?.permissions.length ?? 0} สิทธิ์</span>
        {setPermission.isPending ? <span className="dim" style={{ marginLeft: 'auto', fontSize: 11.5 }}>กำลังบันทึก…</span> : null}
      </div>

      {error ? <div className="form-error" style={{ marginBottom: 12 }}>{error}</div> : null}
      {matrix.isError ? (
        <div className="form-error" style={{ marginBottom: 12 }}>
          {matrix.error instanceof Error ? matrix.error.message : 'โหลดเมทริกซ์สิทธิ์ไม่สำเร็จ'}
        </div>
      ) : null}

      <div className="table-wrap">
        <table className="table data">
          <thead>
            <tr>
              <th style={{ minWidth: 260 }}>สิทธิ์การใช้งาน</th>
              {roles.map((role) => (
                <th key={role} style={{ textAlign: 'center', minWidth: 128, textTransform: 'none' }}>
                  <div style={{ fontSize: 12, letterSpacing: 0, color: 'var(--color-ivory)' }}>
                    {roleLabel(role)}
                  </div>
                  <div className="dim" style={{ fontSize: 10.5, letterSpacing: 0, marginTop: 4, textTransform: 'none' }}>
                    {data?.user_counts[role] ?? 0} คน · {allowedCount(role)}/{data?.permissions.length ?? 0} สิทธิ์
                  </div>
                  {warningsFor(role) > 0 ? (
                    <div style={{ marginTop: 5, fontSize: 10.5, letterSpacing: 0, color: 'var(--color-amber)', textTransform: 'none' }}>
                      {warningsFor(role)} ข้อควรทบทวน
                    </div>
                  ) : null}
                  {role === 'owner' ? (
                    <div className="dim" style={{ fontSize: 10, letterSpacing: 0, marginTop: 4, textTransform: 'none' }}>
                      ถูกล็อก
                    </div>
                  ) : null}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {matrix.isLoading ? (
              <tr>
                <td colSpan={roles.length + 1} style={{ padding: 16 }}><Skel h={280} /></td>
              </tr>
            ) : (
              groups.map(([group, perms]) => (
                <Fragment key={`group-${group}`}>
                  <tr style={{ cursor: 'default' }}>
                    <td colSpan={roles.length + 1} className="dim" style={{
                      fontSize: 10.5, letterSpacing: '.12em', textTransform: 'uppercase',
                      paddingTop: 10, paddingBottom: 10, background: 'var(--color-wash)',
                    }}>
                      {group}
                    </td>
                  </tr>
                  {perms.map((p) => (
                    <tr key={p.code} style={{ cursor: 'default' }}>
                      <td>
                        <div style={{ fontSize: 12.5 }}>{p.label_th}</div>
                        <div className="mono dim" style={{ fontSize: 10.5, marginTop: 3 }}>{p.code}</div>
                      </td>
                      {roles.map((role) => {
                        const cell = cellMap.get(`${role}:${p.code}`)
                        return (
                          <td key={role} style={{
                            textAlign: 'center',
                            background: cell?.overridden ? 'rgba(200, 169, 106, 0.06)' : undefined,
                          }}>
                            <input
                              type="checkbox"
                              checked={cell?.allowed ?? false}
                              disabled={role === 'owner' || setPermission.isPending}
                              onChange={(e) => toggle(role, p.code, e.target.checked)}
                              aria-label={`${roleLabel(role)} — ${p.label_th}`}
                              title={role === 'owner' ? 'เจ้าขององค์กรถูกจำกัดสิทธิ์ไม่ได้' : undefined}
                              style={{
                                width: 15, height: 15, accentColor: 'var(--color-gold)',
                                cursor: role === 'owner' ? 'not-allowed' : 'pointer',
                              }}
                            />
                          </td>
                        )
                      })}
                    </tr>
                  ))}
                </Fragment>
              ))
            )}
          </tbody>
        </table>
      </div>

      <div className="row" style={{ marginTop: 12, gap: 18, flexWrap: 'wrap' }}>
        <span className="dim" style={{ fontSize: 11.5 }}>ติ๊ก = ให้สิทธิ์ในบทบาทนั้น</span>
        <span className="dim" style={{ fontSize: 11.5 }}>พื้นหลังทองอ่อน = แก้ไขจากค่าเริ่มต้นของระบบ</span>
        <span className="dim" style={{ fontSize: 11.5 }}>คอลัมน์เจ้าขององค์กรถูกล็อกเพราะถูกจำกัดสิทธิ์ไม่ได้</span>
      </div>
    </>
  )
}
